import { Box, Button, Card, CardContent, Chip, Divider, Stack, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { UserRole } from '@tnf-recruit/shared'
import { useUser } from '../../hooks/useUser'
import { useSubscription } from '../../hooks/useSubscription'

const ROLE_LABELS: Record<string, string> = {
  [UserRole.STUDENT]: 'Athlete',
  [UserRole.RECRUITER]: 'Coach / Recruiter',
  [UserRole.ADMIN]: 'Admin',
}

const InfoRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 1 }}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right' }}>
      {value}
    </Typography>
  </Box>
)

/** Read-only account summary; recruiters also see their subscription status. */
export const AccountCard = () => {
  const navigate = useNavigate()
  const { data: dbUser, isLoading } = useUser()
  const isRecruiter = dbUser?.role === UserRole.RECRUITER
  const { subscription } = useSubscription({ enabled: isRecruiter })

  const memberSince = dbUser?.createdAt
    ? new Date(dbUser.createdAt).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '—'

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Account
        </Typography>

        {isLoading ? (
          <Typography variant="body2" color="text.secondary">
            Loading…
          </Typography>
        ) : (
          <Stack divider={<Divider />}>
            <InfoRow label="Email" value={dbUser?.email ?? '—'} />
            <InfoRow label="Account type" value={ROLE_LABELS[dbUser?.role ?? ''] ?? '—'} />
            <InfoRow label="Member since" value={memberSince} />

            {isRecruiter && (
              <Box sx={{ py: 1 }}>
                <Box
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Subscription
                  </Typography>
                  {subscription.isLoading ? (
                    <Typography variant="body2">—</Typography>
                  ) : subscription.data?.status === 'active' ? (
                    <Chip label="Active" color="success" size="small" />
                  ) : (
                    <Chip label="No active subscription" size="small" />
                  )}
                </Box>
                {subscription.data?.status === 'active' && subscription.data?.currentPeriodEnd && (
                  <Typography variant="caption" color="text.secondary">
                    Renews {new Date(subscription.data.currentPeriodEnd).toLocaleDateString()}
                  </Typography>
                )}
                <Box sx={{ mt: 1 }}>
                  <Button size="small" variant="outlined" onClick={() => navigate('/subscription')}>
                    Manage subscription
                  </Button>
                </Box>
              </Box>
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  )
}
